import React, { useContext, useEffect } from 'react';
import { SttSeeButton, SttHidden, SttTranslateHook, SttCtrlP, SttCircularProgress } from '@stt-componentes/core';
import {
	SttTable,
	SttTableHead,
	SttTableBody,
	SttTableRow,
	SttTableCell,
	SttTableSortLabel,
	SttTablePagination,
	SttContainerTitle,
	SttTableCard
} from '@stt-componentes/core-v2';
import Form from './form';
import Detalhes from './detalhes';
import { batch, signal } from '@preact/signals-react';
import { useSignals } from '@preact/signals-react/runtime';
import { NotificationManager } from 'react-notifications';

const ORDENACAO = {
	DIRECAO: {
		ASC: 'asc',
		DESC: 'desc'
	},
	CAMPOS: {
		DATA: 'data',
		SOLICITANTE: 'solicitante',
		PACIENTE: 'nome_paciente',
		CIDADE_PROCEDENCIA: 'nome_cidade'
	}
};

export const idSolicitacaoSelecionada = signal(null);
export const solicitacoes = signal([]);
export const totalRegistros = signal(0);
export const buscaEmAndamento = signal(false);
export const page = signal(null);
export const count = signal(null);
export const order = signal(ORDENACAO.DIRECAO.DESC);
export const orderBy = signal(ORDENACAO.CAMPOS.DATA);
export const buscar = signal(false);
export const visualizarSolicitacao = signal(null);

const SolicitacoesEmAberto = () => {
	const { strings } = useContext(SttTranslateHook.I18nContext);
	useSignals();

	useEffect(() => {
		batch(() => {
			page.value ??= global.gConfig.pagination.start;
			count.value ??= global.gConfig.pagination.count;
		});
	}, []);

	const resetPageOrder = () => {
		batch(() => {
			page.value = global.gConfig.pagination.start;
			count.value = global.gConfig.pagination.count;
			order.value = ORDENACAO.DIRECAO.DESC;
			orderBy.value = ORDENACAO.CAMPOS.DATA;
		});
	};

	const handleClickSort = (campo) => {
		const isAsc = orderBy.value === campo && order.value === ORDENACAO.DIRECAO.ASC;
		const newOrder = isAsc ? ORDENACAO.DIRECAO.DESC : ORDENACAO.DIRECAO.ASC;
		batch(() => {
			order.value = newOrder;
			orderBy.value = campo;
			buscar.value = true;
		});
	};

	const handleChangePage = (event, newPage) => {
		batch(() => {
			page.value = newPage;
			buscar.value = true;
		});
	};

	const handleChangeRowsPerPage = (event) => {
		batch(() => {
			count.value = event.target.value;
			page.value = global.gConfig.pagination.start;
			buscar.value = true;
		});
	};

	const callback = () => {
		idSolicitacaoSelecionada.value = null;
	};

	const callbackBusca = (dados) => {
		batch(() => {
			if (dados) {
				totalRegistros.value = parseInt(dados.totalRegistros);
				solicitacoes.value = dados.itens;
			} else {
				totalRegistros.value = 0;
				solicitacoes.value = [];
			}
			buscar.value = false;
		});
	};

	const handleClickVoltar = (atualizarBusca) => {
		batch(() => {
			idSolicitacaoSelecionada.value = null;
			if (atualizarBusca) {
				buscar.value = true;
			}
		});
	};

	const callbackImpedirImpressao = () => {
		NotificationManager.error(strings.impressaoDesabilitada);
	};

	return (
		<>
			{
				idSolicitacaoSelecionada.value === null
					?
					<>
						<SttCtrlP callback={callbackImpedirImpressao} />
						<SttContainerTitle titulo={strings.pesquisaSolicitacoesExame} />
						<Form
							callbackBusca={callbackBusca}
							resetPageOrder={resetPageOrder}
						/>
						<SttHidden smDown>
							<SttTable>
								<SttTableHead>
									<SttTableRow>
										<SttTableCell
											width="10%"
											sortDirection={orderBy.value === ORDENACAO.CAMPOS.DATA ? order.value : false}
										>
											<SttTableSortLabel
												active={orderBy === ORDENACAO.CAMPOS.DATA}
												direction={orderBy === ORDENACAO.CAMPOS.DATA ? order : ORDENACAO.DIRECAO.ASC}
												onClick={() => handleClickSort(ORDENACAO.CAMPOS.DATA)}
											>
												{strings.dataSolicitacao}
											</SttTableSortLabel>
										</SttTableCell>
										<SttTableCell
											width="25%"
											sortDirection={orderBy.value === ORDENACAO.CAMPOS.SOLICITANTE ? order.value : false}
										>
											<SttTableSortLabel
												active={orderBy.value === ORDENACAO.CAMPOS.SOLICITANTE}
												direction={orderBy.value === ORDENACAO.CAMPOS.SOLICITANTE ? order.value : ORDENACAO.DIRECAO.ASC}
												onClick={() => handleClickSort(ORDENACAO.CAMPOS.SOLICITANTE)}
											>
												{strings.solicitante}
											</SttTableSortLabel>
										</SttTableCell>
										<SttTableCell
											width="25%"
											sortDirection={orderBy.value === ORDENACAO.CAMPOS.PACIENTE ? order.value : false}
										>
											<SttTableSortLabel
												active={orderBy.value === ORDENACAO.CAMPOS.PACIENTE}
												direction={orderBy.value === ORDENACAO.CAMPOS.PACIENTE ? order.value : ORDENACAO.DIRECAO.ASC}
												onClick={() => handleClickSort(ORDENACAO.CAMPOS.PACIENTE)}
											>
												{strings.paciente}
											</SttTableSortLabel>
										</SttTableCell>
										<SttTableCell
											width="15%"
											sortDirection={orderBy.value === ORDENACAO.CAMPOS.CIDADE_PROCEDENCIA ? order.value : false}
										>
											<SttTableSortLabel
												active={orderBy.value === ORDENACAO.CAMPOS.CIDADE_PROCEDENCIA}
												direction={orderBy.value === ORDENACAO.CAMPOS.CIDADE_PROCEDENCIA ? order.value : ORDENACAO.DIRECAO.ASC}
												onClick={() => handleClickSort(ORDENACAO.CAMPOS.CIDADE_PROCEDENCIA)}
											>
												{`${strings.municipio}/${strings.uf}`}
											</SttTableSortLabel>
										</SttTableCell>
										<SttTableCell width="5%">{strings.visualizar}</SttTableCell>
									</SttTableRow>
								</SttTableHead>
								<SttTableBody>
									{buscaEmAndamento.value ? (
										<SttTableRow key={-1}>
											<SttTableCell
												colSpan={6}
												align="center"
											>
												<SttCircularProgress />
											</SttTableCell>
										</SttTableRow>
									) : solicitacoes.value.length === 0 ? (
										<SttTableRow key={-1}>
											<SttTableCell
												colSpan={6}
												align="center"
											>
												{strings.nenhumRegistroEncontrado}
											</SttTableCell>
										</SttTableRow>
									) : (
										solicitacoes.value.map((row, index) => (
											<SttTableRow key={index}>
												<SttTableCell>{`${row.data_formatada} ${global.gConfig.sufixo_data_hora}`}</SttTableCell>
												<SttTableCell>{row.solicitante}</SttTableCell>
												<SttTableCell>{row.nome_paciente}</SttTableCell>
												<SttTableCell>{row.nome_cidade_estado}</SttTableCell>
												<SttTableCell align="center">
													<SttSeeButton
														id={`btn-ver-solicitacao-${index}`}
														onClick={() => {
															idSolicitacaoSelecionada.value = row.id;
														}}
													/>
												</SttTableCell>
											</SttTableRow>
										))
									)}
								</SttTableBody>
							</SttTable>
						</SttHidden>
						<SttHidden mdUp>
							{buscaEmAndamento.value || solicitacoes.value.length === 0 ? (
								<SttTableCard
									carregando={buscaEmAndamento.value}
									nenhumRegistroEncontrado={solicitacoes.value.length === 0 ? strings.nenhumRegistroEncontrado : null}
								/>
							) : (
								solicitacoes.value.map((row, index) => (
									<SttTableCard
										key={index}
										titulo={row.nome_paciente}
										acao={
											<SttSeeButton
												onClick={() => {
													idSolicitacaoSelecionada.value = row.id;
												}}
											/>
										}
										conteudo={[
											[
												{ titulo: strings.data, valor: `${row.data_formatada} ${global.gConfig.sufixo_data_hora}`, xs: 6 },
												{
													titulo: `${strings.municipio}/${strings.uf}`,
													valor: row.nome_cidade_estado,
													xs: 6
												}
											],
											[{ titulo: strings.solicitante, valor: row.solicitante }]
										]}
									/>
								))
							)}
						</SttHidden>
						{solicitacoes.value.length > 0 ? (
							<SttTablePagination
								rowsPerPageOptions={[10, 20, 40]}
								component="div"
								count={totalRegistros.value}
								rowsPerPage={count.value}
								page={page.value}
								onPageChange={handleChangePage}
								onRowsPerPageChange={handleChangeRowsPerPage}
								labelRowsPerPage={strings.linhasPorPagina}
								labelDisplayedRows={(params) => `${params.from}-${params.to} ${strings.de} ${params.count}`}
							/>
						) : null}
					</>
					:
					<Detalhes
						nutricao={idSolicitacaoSelecionada.value}
						callback={callback}
					/>
			}
		</>
	);
};

export default SolicitacoesEmAberto;
